// Localized legal copy for Ibeflix, keyed by the language name that each country
// in src/data/countries.js carries (e.g. "English", "Spanish", "Swahili", "Yoruba").
// Any field not translated for a language automatically falls back to English via getLegal().

export const PIRACY_DOMAINS = [
  "skyflix.to", "123movies", "putlocker", "fmovies", "soap2day", "primewire",
  "gomovies", "yesmovies", "losmovies", "watchseries", "sflix", "hurawatch",
  "movies123", "flixtor", "1movies", "0123movies",
];

export const RTL_LANGUAGES = new Set(["Arabic", "Hebrew", "Persian", "Urdu"]);

const EN = {
  noticeTitle: "⚠️ Legal Notice",
  noticeIntro: "Ibeflix is a watch-party tool for LEGAL streaming services only (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
  noticeDontShare: "Don't share links to pirated content. Users who do may be banned.",
  noticeAgreeLine: "By continuing you agree to our Terms of Service.",
  readTerms: "Read Terms",
  agree: "I Agree",
  decline: "Decline",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  contact: "Contact",
  report: "Report Abuse",
  clause: [
    "Ibeflix is a watch-party sync tool for LEGAL streaming services only (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
    "You are responsible for ensuring you have the right to access any content you share. Ibeflix does not facilitate, enable, or endorse piracy or copyright infringement.",
    "Sharing links to pirated content (e.g. skyflix.to, 123movies, putlocker) violates these terms. Users who share pirated content may be banned from Ibeflix.",
  ],
};

// Only the user-facing strings that differ are listed; the rest fall back to English.
export const LEGAL = {
  English: EN,

  Spanish: {
    noticeTitle: "⚠️ Aviso Legal",
    noticeIntro: "Ibeflix es una herramienta para ver juntos solo en servicios de streaming LEGALES (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
    noticeDontShare: "No compartas enlaces a contenido pirata. Quienes lo hagan pueden ser expulsados.",
    noticeAgreeLine: "Al continuar aceptas nuestros Términos del Servicio.",
    readTerms: "Leer Términos", agree: "Acepto", decline: "Rechazar",
    terms: "Términos del Servicio", privacy: "Política de Privacidad", contact: "Contacto", report: "Reportar Abuso",
    clause: [
      "Ibeflix es una herramienta para ver contenido en sincronía solo en servicios de streaming LEGALES (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
      "Eres responsable de asegurarte de tener derecho a acceder a cualquier contenido que compartas. Ibeflix no facilita ni promueve la piratería ni la infracción de derechos de autor.",
      "Compartir enlaces a contenido pirata (p. ej. skyflix.to, 123movies, putlocker) viola estos términos. Los usuarios que lo hagan pueden ser expulsados de Ibeflix.",
    ],
  },

  Portuguese: {
    noticeTitle: "⚠️ Aviso Legal",
    noticeIntro: "O Ibeflix é uma ferramenta para assistir juntos apenas em serviços de streaming LEGAIS (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
    noticeDontShare: "Não compartilhe links de conteúdo pirata. Quem fizer isso pode ser banido.",
    noticeAgreeLine: "Ao continuar, você concorda com nossos Termos de Serviço.",
    readTerms: "Ler Termos", agree: "Concordo", decline: "Recusar",
    terms: "Termos de Serviço", privacy: "Política de Privacidade", contact: "Contato", report: "Denunciar Abuso",
    clause: [
      "O Ibeflix é uma ferramenta de sincronização para assistir junto apenas em serviços de streaming LEGAIS (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
      "Você é responsável por garantir que tem o direito de acessar qualquer conteúdo que compartilhar. O Ibeflix não facilita nem endossa a pirataria ou violação de direitos autorais.",
      "Compartilhar links de conteúdo pirata (ex.: skyflix.to, 123movies, putlocker) viola estes termos. Usuários que o fizerem podem ser banidos do Ibeflix.",
    ],
  },

  French: {
    noticeTitle: "⚠️ Avis Juridique",
    noticeIntro: "Ibeflix est un outil de visionnage en groupe uniquement pour les services de streaming LÉGAUX (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
    noticeDontShare: "Ne partagez pas de liens vers du contenu piraté. Les contrevenants peuvent être bannis.",
    noticeAgreeLine: "En continuant, vous acceptez nos Conditions d'utilisation.",
    readTerms: "Lire les conditions", agree: "J'accepte", decline: "Refuser",
    terms: "Conditions d'utilisation", privacy: "Politique de confidentialité", contact: "Contact", report: "Signaler un abus",
    clause: [
      "Ibeflix est un outil de synchronisation pour regarder ensemble uniquement sur des services de streaming LÉGAUX (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, etc.).",
      "Vous êtes responsable de vous assurer que vous avez le droit d'accéder au contenu que vous partagez. Ibeflix ne facilite ni n'encourage le piratage ou la violation du droit d'auteur.",
      "Partager des liens vers du contenu piraté (ex. skyflix.to, 123movies, putlocker) viole ces conditions. Les utilisateurs concernés peuvent être bannis d'Ibeflix.",
    ],
  },

  German: {
    noticeTitle: "⚠️ Rechtlicher Hinweis",
    noticeIntro: "Ibeflix ist ein Watch-Party-Tool nur für LEGALE Streaming-Dienste (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV usw.).",
    noticeDontShare: "Teile keine Links zu raubkopierten Inhalten. Verstöße können zur Sperrung führen.",
    noticeAgreeLine: "Mit dem Fortfahren stimmst du unseren Nutzungsbedingungen zu.",
    readTerms: "Bedingungen lesen", agree: "Ich stimme zu", decline: "Ablehnen",
    terms: "Nutzungsbedingungen", privacy: "Datenschutz", contact: "Kontakt", report: "Missbrauch melden",
  },

  Italian: {
    noticeTitle: "⚠️ Avviso Legale",
    noticeIntro: "Ibeflix è uno strumento per guardare insieme solo su servizi di streaming LEGALI (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, ecc.).",
    noticeDontShare: "Non condividere link a contenuti pirata. Chi lo fa può essere bandito.",
    noticeAgreeLine: "Continuando accetti i nostri Termini di Servizio.",
    readTerms: "Leggi i termini", agree: "Accetto", decline: "Rifiuta",
    terms: "Termini di Servizio", privacy: "Informativa sulla Privacy", contact: "Contatti", report: "Segnala Abuso",
  },

  Dutch: {
    noticeTitle: "⚠️ Juridische Kennisgeving",
    noticeIntro: "Ibeflix is een kijk-samen-tool alleen voor LEGALE streamingdiensten (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, enz.).",
    noticeDontShare: "Deel geen links naar illegale content. Overtreders kunnen worden verbannen.",
    noticeAgreeLine: "Door verder te gaan ga je akkoord met onze Servicevoorwaarden.",
    readTerms: "Lees voorwaarden", agree: "Ik ga akkoord", decline: "Weigeren",
    terms: "Servicevoorwaarden", privacy: "Privacybeleid", contact: "Contact", report: "Misbruik melden",
  },

  Polish: {
    noticeTitle: "⚠️ Informacja Prawna",
    noticeIntro: "Ibeflix to narzędzie do wspólnego oglądania wyłącznie w LEGALNYCH serwisach streamingowych (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV itp.).",
    noticeDontShare: "Nie udostępniaj linków do pirackich treści. Grozi to banem.",
    noticeAgreeLine: "Kontynuując, akceptujesz nasz Regulamin.",
    readTerms: "Przeczytaj regulamin", agree: "Zgadzam się", decline: "Odrzuć",
    terms: "Regulamin", privacy: "Polityka Prywatności", contact: "Kontakt", report: "Zgłoś nadużycie",
  },

  Russian: {
    noticeTitle: "⚠️ Правовое уведомление",
    noticeIntro: "Ibeflix — инструмент для совместного просмотра только ЛЕГАЛЬНЫХ стриминговых сервисов (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV и др.).",
    noticeDontShare: "Не делитесь ссылками на пиратский контент. Нарушители могут быть заблокированы.",
    noticeAgreeLine: "Продолжая, вы соглашаетесь с нашими Условиями использования.",
    readTerms: "Читать условия", agree: "Я согласен", decline: "Отклонить",
    terms: "Условия использования", privacy: "Политика конфиденциальности", contact: "Контакты", report: "Пожаловаться",
  },

  Turkish: {
    noticeTitle: "⚠️ Yasal Uyarı",
    noticeIntro: "Ibeflix yalnızca YASAL yayın hizmetleri (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV vb.) için bir birlikte izleme aracıdır.",
    noticeDontShare: "Korsan içeriğe bağlantı paylaşmayın. Paylaşanlar yasaklanabilir.",
    noticeAgreeLine: "Devam ederek Hizmet Şartlarımızı kabul etmiş olursunuz.",
    readTerms: "Şartları oku", agree: "Kabul ediyorum", decline: "Reddet",
    terms: "Hizmet Şartları", privacy: "Gizlilik Politikası", contact: "İletişim", report: "Kötüye kullanımı bildir",
  },

  Arabic: {
    noticeTitle: "⚠️ إشعار قانوني",
    noticeIntro: "Ibeflix أداة للمشاهدة الجماعية لخدمات البث القانونية فقط (YouTube، Netflix، Disney+، Hulu، Tubi، Pluto TV، إلخ).",
    noticeDontShare: "لا تشارك روابط لمحتوى مقرصن. قد يتم حظر من يفعل ذلك.",
    noticeAgreeLine: "بالمتابعة فإنك توافق على شروط الخدمة الخاصة بنا.",
    readTerms: "اقرأ الشروط", agree: "أوافق", decline: "رفض",
    terms: "شروط الخدمة", privacy: "سياسة الخصوصية", contact: "اتصل بنا", report: "الإبلاغ عن إساءة",
    clause: [
      "Ibeflix أداة لمزامنة المشاهدة الجماعية لخدمات البث القانونية فقط (YouTube، Netflix، Disney+، Hulu، Tubi، Pluto TV، إلخ).",
      "أنت مسؤول عن التأكد من امتلاكك الحق في الوصول إلى أي محتوى تشاركه. لا يسهّل Ibeflix القرصنة أو انتهاك حقوق النشر ولا يشجع عليها.",
      "مشاركة روابط المحتوى المقرصن (مثل skyflix.to، 123movies، putlocker) تنتهك هذه الشروط، وقد يتم حظر من يفعل ذلك.",
    ],
  },

  Hindi: {
    noticeTitle: "⚠️ कानूनी सूचना",
    noticeIntro: "Ibeflix केवल वैध स्ट्रीमिंग सेवाओं (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV आदि) के लिए एक साथ देखने का टूल है।",
    noticeDontShare: "पायरेटेड सामग्री के लिंक साझा न करें। ऐसा करने वालों पर प्रतिबंध लग सकता है।",
    noticeAgreeLine: "जारी रखकर आप हमारी सेवा शर्तों से सहमत होते हैं।",
    readTerms: "शर्तें पढ़ें", agree: "मैं सहमत हूँ", decline: "अस्वीकार करें",
    terms: "सेवा की शर्तें", privacy: "गोपनीयता नीति", contact: "संपर्क", report: "दुरुपयोग की रिपोर्ट करें",
  },

  Chinese: {
    noticeTitle: "⚠️ 法律声明",
    noticeIntro: "Ibeflix 是仅用于合法流媒体服务（YouTube、Netflix、Disney+、Hulu、Tubi、Pluto TV 等）的一起看工具。",
    noticeDontShare: "请勿分享盗版内容链接。违规者可能被封禁。",
    noticeAgreeLine: "继续即表示您同意我们的服务条款。",
    readTerms: "阅读条款", agree: "我同意", decline: "拒绝",
    terms: "服务条款", privacy: "隐私政策", contact: "联系我们", report: "举报滥用",
    clause: [
      "Ibeflix 是仅用于合法流媒体服务（YouTube、Netflix、Disney+、Hulu、Tubi、Pluto TV 等）的同步一起看工具。",
      "您有责任确保您有权访问所分享的任何内容。Ibeflix 不促成、不支持任何盗版或侵犯版权的行为。",
      "分享盗版内容链接（如 skyflix.to、123movies、putlocker）违反本条款，相关用户可能被封禁。",
    ],
  },

  Japanese: {
    noticeTitle: "⚠️ 法的通知",
    noticeIntro: "Ibeflix は合法的なストリーミングサービス（YouTube、Netflix、Disney+、Hulu、Tubi、Pluto TV など）専用の一緒に視聴するツールです。",
    noticeDontShare: "海賊版コンテンツのリンクを共有しないでください。違反者は利用停止となる場合があります。",
    noticeAgreeLine: "続行すると利用規約に同意したことになります。",
    readTerms: "規約を読む", agree: "同意する", decline: "拒否",
    terms: "利用規約", privacy: "プライバシーポリシー", contact: "お問い合わせ", report: "不正利用を報告",
  },

  Korean: {
    noticeTitle: "⚠️ 법적 고지",
    noticeIntro: "Ibeflix는 합법적인 스트리밍 서비스(YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV 등)만을 위한 함께 보기 도구입니다.",
    noticeDontShare: "불법 복제 콘텐츠 링크를 공유하지 마세요. 위반 시 차단될 수 있습니다.",
    noticeAgreeLine: "계속하면 서비스 약관에 동의하는 것입니다.",
    readTerms: "약관 읽기", agree: "동의합니다", decline: "거부",
    terms: "서비스 약관", privacy: "개인정보 처리방침", contact: "문의", report: "악용 신고",
  },

  Indonesian: {
    noticeTitle: "⚠️ Pemberitahuan Hukum",
    noticeIntro: "Ibeflix adalah alat nonton bareng hanya untuk layanan streaming LEGAL (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, dll.).",
    noticeDontShare: "Jangan bagikan tautan konten bajakan. Pelanggar dapat diblokir.",
    noticeAgreeLine: "Dengan melanjutkan, Anda menyetujui Ketentuan Layanan kami.",
    readTerms: "Baca Ketentuan", agree: "Saya Setuju", decline: "Tolak",
    terms: "Ketentuan Layanan", privacy: "Kebijakan Privasi", contact: "Kontak", report: "Laporkan Penyalahgunaan",
  },

  Filipino: {
    noticeTitle: "⚠️ Legal na Paunawa",
    noticeIntro: "Ang Ibeflix ay tool para sa sabay-sabay na panonood sa LEGAL na streaming services lamang (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, atbp.).",
    noticeDontShare: "Huwag mag-share ng link sa pirated na content. Maaaring ma-ban ang gagawa nito.",
    noticeAgreeLine: "Sa pagpapatuloy, sumasang-ayon ka sa aming Mga Tuntunin ng Serbisyo.",
    readTerms: "Basahin ang Tuntunin", agree: "Sumasang-ayon Ako", decline: "Tanggihan",
    terms: "Mga Tuntunin ng Serbisyo", privacy: "Patakaran sa Privacy", contact: "Makipag-ugnayan", report: "Iulat ang Pang-aabuso",
  },

  Vietnamese: {
    noticeTitle: "⚠️ Thông Báo Pháp Lý",
    noticeIntro: "Ibeflix là công cụ xem chung chỉ dành cho các dịch vụ phát trực tuyến HỢP PHÁP (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, v.v.).",
    noticeDontShare: "Không chia sẻ liên kết nội dung lậu. Người vi phạm có thể bị cấm.",
    noticeAgreeLine: "Bằng việc tiếp tục, bạn đồng ý với Điều khoản Dịch vụ của chúng tôi.",
    readTerms: "Đọc điều khoản", agree: "Tôi đồng ý", decline: "Từ chối",
    terms: "Điều khoản Dịch vụ", privacy: "Chính sách Bảo mật", contact: "Liên hệ", report: "Báo cáo Lạm dụng",
  },

  Thai: {
    noticeTitle: "⚠️ ประกาศทางกฎหมาย",
    noticeIntro: "Ibeflix เป็นเครื่องมือดูพร้อมกันสำหรับบริการสตรีมมิงที่ถูกกฎหมายเท่านั้น (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV ฯลฯ)",
    noticeDontShare: "อย่าแชร์ลิงก์เนื้อหาละเมิดลิขสิทธิ์ ผู้ที่ทำอาจถูกแบน",
    noticeAgreeLine: "การดำเนินการต่อถือว่าคุณยอมรับข้อกำหนดการให้บริการของเรา",
    readTerms: "อ่านข้อกำหนด", agree: "ฉันยอมรับ", decline: "ปฏิเสธ",
    terms: "ข้อกำหนดการให้บริการ", privacy: "นโยบายความเป็นส่วนตัว", contact: "ติดต่อ", report: "รายงานการละเมิด",
  },

  Swahili: {
    noticeTitle: "⚠️ Onyo la Kisheria",
    noticeIntro: "Ibeflix ni zana ya kutazama pamoja kwa huduma HALALI za utiririshaji pekee (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, n.k.).",
    noticeDontShare: "Usishiriki viungo vya maudhui ya wizi. Wanaofanya hivyo wanaweza kupigwa marufuku.",
    noticeAgreeLine: "Kwa kuendelea unakubali Masharti yetu ya Huduma.",
    readTerms: "Soma Masharti", agree: "Nakubali", decline: "Kataa",
    terms: "Masharti ya Huduma", privacy: "Sera ya Faragha", contact: "Wasiliana", report: "Ripoti Unyanyasaji",
    clause: [
      "Ibeflix ni zana ya kusawazisha utazamaji wa pamoja kwa huduma HALALI za utiririshaji pekee (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, n.k.).",
      "Wewe ndiye unayewajibika kuhakikisha una haki ya kufikia maudhui yoyote unayoshiriki. Ibeflix hairahisishi wala kuunga mkono wizi au ukiukaji wa hakimiliki.",
      "Kushiriki viungo vya maudhui ya wizi (mf. skyflix.to, 123movies, putlocker) kunakiuka masharti haya. Watumiaji wanaofanya hivyo wanaweza kupigwa marufuku Ibeflix.",
    ],
  },

  Yoruba: {
    noticeTitle: "⚠️ Ìkìlọ̀ Òfin",
    noticeIntro: "Ibeflix jẹ́ irinṣẹ́ ìwòran papọ̀ fún àwọn iṣẹ́ ìtàkọ̀kọ̀ tó bófin mu nìkan (YouTube, Netflix, Disney+, Hulu, Tubi, Pluto TV, àti bẹ́ẹ̀ bẹ́ẹ̀ lọ).",
    noticeDontShare: "Má ṣe pín àwọn ọ̀nà àsopọ̀ sí àkóónú tí a jí gbé. Àwọn tó bá ṣe é lè dí ìdíwọ́ mọ́.",
    noticeAgreeLine: "Nípa títẹ̀síwájú o gbà pẹ̀lú Àwọn Òfin Iṣẹ́ wa.",
    readTerms: "Ka Àwọn Òfin", agree: "Mo Gbà", decline: "Kọ̀",
    terms: "Àwọn Òfin Iṣẹ́", privacy: "Òfin Àṣírí", contact: "Kàn sí wa", report: "Ròyìn Ìlòkulò",
  },
};

// Returns the legal copy for a language, falling back to English for anything missing.
export function getLegal(language) {
  return { ...EN, ...(LEGAL[language] || {}) };
}

export function isRTL(language) {
  return RTL_LANGUAGES.has(language);
}

export function containsPiracyLink(text) {
  if (!text) return false;
  const t = String(text).toLowerCase();
  return PIRACY_DOMAINS.some((d) => t.includes(d));
}
